import HistoryRounded from '@mui/icons-material/HistoryRounded';
import {
  Box,
  Chip,
  FormControlLabel,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { Money } from '../../../components/Money';
import { dateTime } from '../../../lib/format';
import { useShop } from '../../../shop/ShopProvider';
import type { Allocation } from './hooks';
import { ALLOCATION_METHODS, docType } from './labels';
import { DocLink } from './ui';

/**
 * Allocations of one document, seen from that document: for a payment or note the documents it
 * settled; for a refund the credits it paid out. Reversed rows are history, hidden by default.
 */
export function AllocationsTable({
  allocations,
  selfId,
  emptyText = 'Nothing allocated yet.',
}: {
  allocations: readonly Allocation[];
  selfId: string;
  emptyText?: string;
}) {
  const { format } = useShop();
  const [history, setHistory] = useState(false);
  const reversedCount = allocations.filter((a) => a.reversed).length;
  const rows = allocations.filter((a) => history || !a.reversed);
  return (
    <Box>
      {reversedCount > 0 && (
        <Stack direction="row" sx={{ px: 2.5, py: 1, justifyContent: 'flex-end' }}>
          <FormControlLabel
            control={<Switch size="small" checked={history} onChange={(_, v) => setHistory(v)} />}
            label={
              <Typography variant="body2" color="text.secondary">
                Show history ({reversedCount} reversed)
              </Typography>
            }
          />
        </Stack>
      )}
      {rows.length === 0 ? (
        <Stack spacing={1} sx={{ alignItems: 'center', py: 5, color: 'text.secondary' }}>
          <HistoryRounded sx={{ color: 'text.disabled' }} />
          <Typography variant="body2">{emptyText}</Typography>
        </Stack>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Document</TableCell>
                <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>How</TableCell>
                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>When</TableCell>
                <TableCell align="right">Amount</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((a) => {
                const other = a.credit.id === selfId ? a.target : a.credit;
                return (
                  <TableRow
                    key={a.id}
                    sx={{
                      opacity: a.reversed ? 0.55 : 1,
                      '&:last-child td': { borderBottom: 0 },
                    }}
                  >
                    <TableCell>
                      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                        <Box>
                          <DocLink type={other.type} id={other.id} number={other.number} />
                          <Typography variant="caption" color="text.secondary" component="div">
                            {docType(other.type)}
                            {a.reason ? ` · ${a.reason}` : ''}
                          </Typography>
                        </Box>
                        {a.reversed && <Chip size="small" variant="outlined" label="Reversed" />}
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                      <Chip
                        size="small"
                        variant="outlined"
                        label={ALLOCATION_METHODS[a.method] ?? a.method}
                      />
                    </TableCell>
                    <TableCell
                      sx={{ display: { xs: 'none', md: 'table-cell' }, color: 'text.secondary' }}
                    >
                      {dateTime(a.createdAt, format)}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ textDecoration: a.reversed ? 'line-through' : undefined }}
                    >
                      <Money value={a.amount} strong={!a.reversed} />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
